import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import {
  FileSignature, Search, RefreshCw, Bell, Trash2, ExternalLink,
  CheckCircle2, Clock, XCircle, AlertCircle, Send,
} from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/lib/confirmContext";
import {
  api,
  type SignatureRequest,
  type AgreementTemplate,
} from "@/lib/api";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { SendForSignatureModal } from "@/components/custom/SendForSignatureModal";

// ─── Status helpers ───────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; color: string; Icon: React.ElementType }> = {
  pending:             { label: "Pending",           color: "bg-amber-100 text-amber-700",  Icon: Clock },
  awaiting_signature:  { label: "Awaiting Signature", color: "bg-blue-100 text-blue-700",   Icon: Clock },
  completed:           { label: "Completed",          color: "bg-green-100 text-green-700", Icon: CheckCircle2 },
  declined:            { label: "Declined",           color: "bg-red-100 text-red-700",     Icon: XCircle },
  expired:             { label: "Expired",            color: "bg-gray-100 text-gray-600",   Icon: AlertCircle },
  voided:              { label: "Voided",             color: "bg-gray-100 text-gray-500",   Icon: XCircle },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? { label: status, color: "bg-gray-100 text-gray-600", Icon: Clock };
  const { label, color, Icon } = cfg;
  return (
    <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium", color)}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

// ─── Row component ────────────────────────────────────────────────────────────

function SignatureRow({
  sr,
  onRemind,
  onVoid,
  onSync,
}: {
  sr: SignatureRequest;
  onRemind: (id: string) => void;
  onVoid: (id: string) => void;
  onSync: (id: string) => void;
}) {
  const isActive = ["pending", "awaiting_signature"].includes(sr.status);
  const isDone = ["completed", "declined", "expired", "voided"].includes(sr.status);

  return (
    <tr className={cn("border-b last:border-0 hover:bg-muted/30 transition-colors", isDone && "opacity-60")}>
      <td className="py-3 px-4">
        <div className="font-medium text-sm">{sr.title}</div>
        {sr.agreement_templates && (
          <div className="text-xs text-muted-foreground mt-0.5">{sr.agreement_templates.name}</div>
        )}
      </td>
      <td className="py-3 px-4">
        {sr.accounts ? (
          <Link href={`/accounts/${sr.account_id}`} className="text-sm text-blue-600 hover:underline">
            {sr.accounts.account_name}
          </Link>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </td>
      <td className="py-3 px-4">
        <div className="space-y-0.5">
          {sr.signers.map((s, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs">
              <span className="text-muted-foreground">{s.name}</span>
              {s.status && s.status !== "pending" && (
                <span className={cn("font-medium", s.status === "completed" ? "text-green-600" : "text-muted-foreground")}>
                  · {s.status}
                </span>
              )}
            </div>
          ))}
        </div>
      </td>
      <td className="py-3 px-4">
        <StatusBadge status={sr.status} />
      </td>
      <td className="py-3 px-4 text-xs text-muted-foreground whitespace-nowrap">
        {format(new Date(sr.created_at), "MMM d, yyyy")}
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-1">
          {isActive && (
            <>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => onSync(sr.id)}>
                <RefreshCw className="w-3 h-3 mr-1" /> Sync
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => onRemind(sr.id)}>
                <Bell className="w-3 h-3 mr-1" /> Remind
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-rose-600 hover:text-rose-700" onClick={() => onVoid(sr.id)}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </>
          )}
          {sr.signwell_document_url && (
            <a href={sr.signwell_document_url} target="_blank" rel="noopener noreferrer">
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs">
                <ExternalLink className="w-3 h-3 mr-1" /> View
              </Button>
            </a>
          )}
          {sr.signwell_audit_trail_url && (
            <a href={sr.signwell_audit_trail_url} target="_blank" rel="noopener noreferrer">
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-green-700">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Audit
              </Button>
            </a>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const FILTERS = ["all", "awaiting_signature", "pending", "completed", "declined", "expired", "voided"];

export default function Signatures() {
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const qc = useQueryClient();

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ["signature-requests", statusFilter],
    queryFn: () => api.getSignatureRequests(statusFilter),
  });

  const { data: templates = [] } = useQuery({
    queryKey: ["agreement-templates"],
    queryFn: () => api.getAgreementTemplates(),
  });

  const remindMutation = useMutation({
    mutationFn: (id: string) => api.sendSignatureReminder(id),
    onSuccess: () => toast({ title: "Reminder sent" }),
    onError: () => toast({ title: "Failed to send reminder", variant: "destructive" }),
  });

  const voidMutation = useMutation({
    mutationFn: (id: string) => api.voidSignatureRequest(id),
    onSuccess: () => {
      toast({ title: "Request voided" });
      qc.invalidateQueries({ queryKey: ["signature-requests"] });
    },
    onError: () => toast({ title: "Failed to void", variant: "destructive" }),
  });

  const syncMutation = useMutation({
    mutationFn: (id: string) => api.syncSignatureRequest(id),
    onSuccess: () => {
      toast({ title: "Status synced" });
      qc.invalidateQueries({ queryKey: ["signature-requests"] });
    },
    onError: () => toast({ title: "Sync failed", variant: "destructive" }),
  });

  const filtered = requests.filter((sr: SignatureRequest) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      sr.title.toLowerCase().includes(q) ||
      sr.accounts?.account_name?.toLowerCase().includes(q) ||
      sr.signers.some((s) => s.name.toLowerCase().includes(q) || (s.email ?? "").toLowerCase().includes(q))
    );
  });

  const pendingCount = requests.filter((r: SignatureRequest) => ["pending", "awaiting_signature"].includes(r.status)).length;

  return (
    <Layout>
      <div className="p-6 space-y-5 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileSignature className="w-6 h-6 text-violet-600" />
            <h1 className="text-xl font-semibold">Signatures</h1>
            {pendingCount > 0 && (
              <Badge variant="secondary" className="bg-amber-100 text-amber-700 text-xs">
                {pendingCount} pending
              </Badge>
            )}
          </div>
          <Button onClick={() => setSendModalOpen(true)} className="gap-1.5">
            <Send className="w-4 h-4" /> Send for Signature
          </Button>
        </div>

        {/* Filters + search */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={cn(
                  "px-3 py-1 rounded-md text-xs font-medium transition-colors capitalize",
                  statusFilter === f ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {f === "all" ? "All" : (STATUS_CONFIG[f]?.label ?? f)}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search documents or clients…"
              className="pl-8 h-8 text-sm"
            />
          </div>
        </div>

        {/* Table */}
        <div className="border rounded-lg overflow-hidden bg-card">
          {isLoading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center space-y-2">
              <FileSignature className="w-10 h-10 text-muted-foreground/40 mx-auto" />
              <p className="text-sm text-muted-foreground">No signature requests found</p>
              <Button variant="outline" size="sm" onClick={() => setSendModalOpen(true)}>
                Send your first agreement
              </Button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-muted-foreground">Document</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-muted-foreground">Client</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-muted-foreground">Signers</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-muted-foreground">Status</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-muted-foreground">Sent</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((sr: SignatureRequest) => (
                  <SignatureRow
                    key={sr.id}
                    sr={sr}
                    onRemind={(id) => remindMutation.mutate(id)}
                    onVoid={async (id) => {
                      if (await askConfirm({
                        title: "Void this signature request?",
                        subject: "signature request",
                        body: "will be voided.",
                        lines: ["The signer will no longer be able to sign."],
                        confirmLabel: "Void request",
                        destructive: true,
                      })) voidMutation.mutate(id);
                    }}
                    onSync={(id) => syncMutation.mutate(id)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {sendModalOpen && (
        <SendForSignatureModal
          templates={templates}
          onClose={() => setSendModalOpen(false)}
          onSent={() => {
            setSendModalOpen(false);
            qc.invalidateQueries({ queryKey: ["signature-requests"] });
          }}
        />
      )}
    </Layout>
  );
}
